import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '__components/Kizen/Input';
import { Button } from '@kizen/kds/Button';
import { validateEmail } from 'components/Inputs/TextInput/presets/EmailAddress';
import {
  Column,
  Card,
  Fields,
  FormWrapper,
  Header,
  InputWrapper,
  Footer,
  SuccessMessage,
  VideoWrapper,
  CTAWrapper,
  Testimonial,
} from './styles';
import { Typography } from '@kizen/kds/Typography';
import { DUPLICATE_RESPONSE_CODE } from '../constants';
import { Spacer } from '@kizen/kds/Spacer';
import OutlookSVG from 'graphics/outlook.svg?react';
import GmailSVG from 'graphics/gmail.svg?react';
import { MarketingVideoEmbed } from 'components/VideoEmbed';
import { BusinessBuilderMailServices, KZN_FRAME_EVENTS } from '../events';

const validateNumber = (value: string) => {
  return value.length > 0 && !isNaN(parseInt(value));
};

interface CompleteSessionForm {
  onConfirm: (params: {
    first_name: string;
    last_name: string;
    email: string;
    company_name: string;
    job_title: string;
    num_employees: number;
  }) => any;
  loading: boolean;
  error: string;
  clearError: () => void;
  isCompleted: boolean;
  padding?: string;
  trackEvent?: (event: string, payload?: any) => void;
}

export const CompleteSessionForm = ({
  onConfirm,
  loading,
  error,
  clearError,
  isCompleted,
  padding,
  trackEvent,
}: CompleteSessionForm) => {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [numberOfEmployees, setNumberOfEmployees] = useState('');

  const isValid = useMemo(() => {
    return (
      firstName.length > 0 &&
      lastName.length > 0 &&
      email.length > 0 &&
      companyName.length > 0 &&
      jobTitle.length > 0 &&
      numberOfEmployees &&
      validateNumber(numberOfEmployees) &&
      validateEmail(email)
    );
  }, [firstName, lastName, email, companyName, jobTitle, numberOfEmployees]);

  const getEmailClickHandler = useCallback(
    (service: BusinessBuilderMailServices) => {
      return () => {
        trackEvent?.(KZN_FRAME_EVENTS.OPEN_EMAIL, service);
      };
    },
    [trackEvent]
  );

  return (
    <Card padding={padding}>
      {isCompleted ? (
        <SuccessMessage>
          <Typography size="3xl">
            {t(
              'We’ve just created your Kizen Account and our AI is building your application!'
            )}
          </Typography>
          <Spacer size={30} />
          <Typography size="xl">
            {t(
              'Check your email for your personal link to set up your password and sign in to the extraordinary!'
            )}
          </Typography>
          <Spacer size={30} />
          <CTAWrapper>
            <a
              href="https://mail.google.com"
              target="_blank"
              rel="noreferrer"
              onClick={getEmailClickHandler('gmail')}
            >
              <GmailSVG />
            </a>
            <a
              href="https://outlook.office.com"
              target="_blank"
              rel="noreferrer"
              onClick={getEmailClickHandler('outlook')}
            >
              <OutlookSVG />
            </a>
          </CTAWrapper>
          <Spacer size={30} />
          <VideoWrapper>
            <MarketingVideoEmbed mediaId="lincyy7szk" height={400} />
          </VideoWrapper>
        </SuccessMessage>
      ) : (
        <>
          <Header>
            <Typography size="3xl">
              {t("You're moments away from accessing the extraordinary")}
            </Typography>
            <Spacer size={30} />
            <Typography size="xl">
              {t(
                'Enter your information below to set up your account, no credit card required'
              )}
            </Typography>
          </Header>
          <FormWrapper padding={padding}>
            <Fields>
              <Column>
                <Input
                  type={Input.INPUT_TYPES.TEXT}
                  label={t('First Name')}
                  value={firstName}
                  onChange={setFirstName}
                  placeholder={t('First Name')}
                  tabIndex={1}
                />
                <Input
                  label={t('Email')}
                  value={email}
                  onChange={setEmail}
                  placeholder={t('Email')}
                  tabIndex={3}
                />
                <InputWrapper error={error !== ''}>
                  <Input
                    label={t('Company Name')}
                    value={companyName}
                    onChange={(value: string) => {
                      clearError();
                      setCompanyName(value);
                    }}
                    placeholder={t('Company Name')}
                    error={error === DUPLICATE_RESPONSE_CODE}
                    tabIndex={5}
                  />
                  {error !== '' && (
                    <Typography size="sm" color="viz-seq-red-3">
                      {error === DUPLICATE_RESPONSE_CODE
                        ? t('Business name already exists')
                        : error}
                    </Typography>
                  )}
                </InputWrapper>
              </Column>
              <Column>
                <Input
                  type={Input.INPUT_TYPES.TEXT}
                  label={t('Last Name')}
                  value={lastName}
                  onChange={setLastName}
                  placeholder={t('Last Name')}
                  tabIndex={2}
                />
                <Input
                  type={Input.INPUT_TYPES.TEXT}
                  label={t('Job Title')}
                  value={jobTitle}
                  onChange={setJobTitle}
                  placeholder={t('Job Title')}
                  tabIndex={4}
                />
                <Input
                  type={Input.INPUT_TYPES.NUMBER}
                  label={t('How many employees work at your company?')}
                  value={numberOfEmployees}
                  onChange={setNumberOfEmployees}
                  placeholder={t('Employee Count')}
                  tabIndex={6}
                />
              </Column>
            </Fields>

            <Footer>
              <Button
                onClick={() =>
                  onConfirm({
                    first_name: firstName,
                    last_name: lastName,
                    email: email,
                    company_name: companyName,
                    job_title: jobTitle,
                    num_employees: validateNumber(numberOfEmployees)
                      ? parseInt(numberOfEmployees)
                      : 0,
                  })
                }
                disabled={
                  !isValid || loading || error === DUPLICATE_RESPONSE_CODE
                }
              >
                {t('Submit')}
              </Button>
            </Footer>
            <Testimonial>
              <div>
                {/* Intentionally not translated */}
                <Typography size="lg">
                  “In my 30 years of IT and Operations leadership I have never
                  seen a platform that can deliver as fast as KIZEN. It’s very
                  impressive. With Kizen, we’ve been able to make even our most
                  difficult to please team members very happy.”
                </Typography>
                <Spacer size={30} />
                <Typography>-COO of Insurance Brokerage</Typography>
              </div>
            </Testimonial>
          </FormWrapper>
        </>
      )}
    </Card>
  );
};
